import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * There is one primary-button pattern in ADVisor and this is it: a left-to-right
 * steel gradient that LIGHTENS on hover and lifts 1px. It never darkens, and
 * nothing scales down on press — press is the hover state held, and
 * confirmation is expressed after the fact with `popIn`, not during the click.
 *
 * Radii are role-based: a button is 10 (`rounded-button`), a small control is 9.
 * A button rounded to the card's 14 looks wrong immediately.
 *
 * `outline` is kept as an alias of `secondary`, and `default` as an alias of
 * `md`, because the unrouted ADVisor views still pass those names.
 */
export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap",
    "font-semibold leading-tight",
    "transition-[background,color,border-color,transform,box-shadow] duration-200",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-gradient-to-r from-steel-600 to-steel-700 text-white",
          "border border-transparent shadow-button-primary",
          "hover:from-steel-500 hover:to-steel-600 hover:-translate-y-px",
        ].join(" "),
        secondary: [
          "bg-white text-neutral-600 border border-neutral-200",
          "hover:bg-steel-50 hover:text-steel-600 hover:border-steel-600",
        ].join(" "),
        outline: [
          "bg-white text-neutral-600 border border-neutral-200",
          "hover:bg-steel-50 hover:text-steel-600 hover:border-steel-600",
        ].join(" "),
        solid: [
          "bg-steel-600 text-white border border-transparent",
          "hover:bg-steel-700",
        ].join(" "),
        ghost: [
          "bg-transparent text-neutral-600 border border-transparent",
          "hover:bg-neutral-50 hover:text-neutral-900",
        ].join(" "),
        destructive: [
          "bg-danger-bg text-danger-fg border border-danger-border",
          "hover:bg-[#FEE2E2]",
        ].join(" "),
        link: [
          "bg-transparent text-steel-600 border border-transparent p-0 shadow-none",
          "hover:text-steel-500 hover:underline underline-offset-4",
        ].join(" "),
      },
      size: {
        sm: "gap-[5px] px-[13px] py-[7px] text-fine rounded-control [&_svg]:size-[13px]",
        md: "gap-2 px-5 py-2.5 text-control rounded-button [&_svg]:size-[15px]",
        default: "gap-2 px-5 py-2.5 text-control rounded-button [&_svg]:size-[15px]",
        lg: "gap-[9px] px-[22px] py-3 text-body rounded-button [&_svg]:size-[17px]",
        icon: "size-9 rounded-control [&_svg]:size-[15px]",
      },
      fullWidth: {
        true: "w-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        // A <button> inside a <form> submits by default, which has surprised
        // this codebase before. Callers that want a submit say so.
        type={asChild ? undefined : (type ?? "button")}
        className={cn(buttonVariants({ variant, size, fullWidth, className }))}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button };
